import { useEffect, useRef, useState } from "react";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { type Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ComboboxChip } from "@vega-core/components/shadcn/combobox";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE } from "../constants";
import {
  type DraggableComboboxChipMetaData,
  type SearchMultiSelectItem,
} from "../types";

import { DropIndicator } from "./DropIndicator";

export function DraggableComboboxChip({
  fieldName,
  item,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  fieldName: string;
  item: SearchMultiSelectItem;
}) {
  const chipRef = useRef<HTMLDivElement>(null);
  const dropTargetRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);

  useEffect(() => {
    const chipElement = chipRef.current as HTMLElement;
    const dropTargetElement = dropTargetRef.current as HTMLElement;

    return combine(
      draggable({
        element: chipElement,
        onDragStart: () => {
          setIsDragging(true);
        },
        onDrop: () => {
          setIsDragging(false);
        },
        getInitialData: () =>
          ({
            fieldName,
            item,
            eventType: DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE,
          }) satisfies DraggableComboboxChipMetaData,
      }),
      dropTargetForElements({
        canDrop: ({ source }) => source.element !== chipElement,
        element: dropTargetElement,
        getData({ input, element }) {
          return attachClosestEdge(
            {
              fieldName,
              item,
              eventType: DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE,
            } satisfies DraggableComboboxChipMetaData,
            {
              element,
              input,
              allowedEdges: ["left", "right"],
            },
          );
        },
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: () => {
          setEdge(null);
        },
      }),
    );
  }, [fieldName, item]);

  return (
    // negative margin needed here to extend the possible drop area of each chip
    <div className="relative p-3 -m-3" ref={dropTargetRef}>
      <ComboboxChip
        className={cnTwMerge(
          "relative z-1 cursor-grab",
          isDragging && "opacity-50",
        )}
        onMouseDown={(e) => {
          // Default behaviour of combobox chip onMouseDown blocks drag & drop
          e.preventBaseUIHandler();
        }}
        ref={chipRef}
        {...props}
      />
      {edge ? <DropIndicator edge={edge} /> : null}
    </div>
  );
}
